import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import TopNav from '../components/TopNav'

// Picks the event's "current" round for the league board: prefer an active
// round, then one still open for entry, then just the newest by round
// number (rounds arrives ordered newest-first). This is a simplified,
// board-level version of the per-player round resolution LMSEventPage does
// for itself -- the league board isn't about any one member's entries, so it
// doesn't need that fuller logic.
function pickCurrentRound(rounds) {
  if (!rounds || rounds.length === 0) return null
  return rounds.find(r => r.status === 'active')
    || rounds.find(r => r.status === 'open_for_entry')
    || rounds[0]
}

function statusBadge(status, inRound) {
  if (!inRound) return <span className="badge-gray">Not in this round</span>
  if (status === 'winner') return <span className="badge-blue">Winner</span>
  if (status === 'eliminated') return <span className="badge-red">Eliminated</span>
  if (status === 'pending_payment') return <span className="badge-amber">Pending payment</span>
  return <span className="badge-green">Alive</span>
}

export default function LeagueDetail() {
  const { eventId, leagueId } = useParams()
  const navigate = useNavigate()

  const [authUser, setAuthUser] = useState(null)
  const [league, setLeague] = useState(null)
  const [notFound, setNotFound] = useState(false)
  const [round, setRound] = useState(null)
  const [currentGw, setCurrentGw] = useState(null)
  const [roundBoard, setRoundBoard] = useState([])
  const [standings, setStandings] = useState([])
  const [loading, setLoading] = useState(true)
  const [leaving, setLeaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    async function load() {
      setLoading(true)
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) { navigate('/login'); return }
      setAuthUser(user)

      // RLS (leagues_read_member) returns this row only if I'm a member --
      // a non-member gets no row, not an error, which is exactly "can't see
      // a private league's board" per the spec's test checklist.
      const { data: leagueData } = await supabase
        .from('lms_leagues')
        .select('*')
        .eq('id', leagueId)
        .maybeSingle()

      if (!leagueData) { setNotFound(true); setLoading(false); return }
      setLeague(leagueData)

      const { data: roundsData } = await supabase
        .from('lms_rounds')
        .select('*')
        .eq('event_id', leagueData.event_id)
        .order('round_number', { ascending: false })

      const chosenRound = pickCurrentRound(roundsData || [])
      setRound(chosenRound)

      let gwId = null
      if (chosenRound) {
        const { data: gwData } = await supabase
          .from('lms_gameweeks')
          .select('*')
          .eq('round_id', chosenRound.id)
          .order('gameweek_number', { ascending: true })

        const openGw = (gwData || []).find(g => g.status !== 'processed') || null
        setCurrentGw(openGw)
        gwId = openGw?.id || null
      }

      const [{ data: boardData, error: boardError }, { data: standingsData, error: standingsError }] = await Promise.all([
        chosenRound
          ? supabase.rpc('get_league_round_board', { p_league_id: leagueId, p_round_id: chosenRound.id, p_gameweek_id: gwId })
          : Promise.resolve({ data: [], error: null }),
        supabase.rpc('get_league_board', { p_league_id: leagueId })
      ])

      if (boardError) setError(boardError.message)
      else if (standingsError) setError(standingsError.message)

      setRoundBoard(boardData || [])
      setStandings(standingsData || [])
      setLoading(false)
    }
    load()
  }, [leagueId, navigate])

  async function handleCopy() {
    if (!league) return
    try {
      await navigator.clipboard.writeText(league.invite_code)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard API unavailable (e.g. non-HTTPS or older mobile browser) --
      // the code is already shown on screen, so this is a soft failure.
    }
  }

  async function handleLeave() {
    if (!authUser || !league) return
    if (!window.confirm(`Leave ${league.name}? You can rejoin later with the invite code.`)) return
    setLeaving(true)
    const { error: leaveError } = await supabase
      .from('lms_league_members')
      .delete()
      .eq('league_id', league.id)
      .eq('user_id', authUser.id)
    setLeaving(false)
    if (leaveError) { setError(leaveError.message); return }
    navigate(`/lms/${eventId}`)
  }

  async function handleDelete() {
    if (!league) return
    if (!window.confirm('Delete this league? This removes it for all members. This cannot be undone.')) return
    setDeleting(true)
    const { error: deleteError } = await supabase.rpc('delete_league', { p_league_id: league.id })
    setDeleting(false)
    if (deleteError) { setError(deleteError.message); return }
    navigate(`/lms/${eventId}`)
  }

  if (loading) return <p className="text-muted" style={{ padding: '40px', textAlign: 'center' }}>Loading...</p>

  if (notFound) {
    return (
      <div>
        <TopNav active="my-events" />
        <div className="container-narrow">
          <p className="text-muted">League not found, or you're not a member.</p>
          <a href={`/lms/${eventId}`} className="btn btn-secondary mt-16">Back to event</a>
        </div>
      </div>
    )
  }

  return (
    <div>
      <TopNav active="my-events" />
      <div className="container-narrow">
        <a href={`/lms/${eventId}`} className="text-muted" style={{ fontSize: '13px', textDecoration: 'none' }}>&larr; Back to event</a>

        <h1 style={{ fontFamily: 'var(--ff-d)', fontSize: '26px', fontWeight: '700', margin: '10px 0 4px' }}>
          {league.name}
        </h1>
        <p className="text-muted" style={{ marginBottom: '18px', fontSize: '13px' }}>
          Private league &middot; results only, no separate pot or fees
        </p>

        <div className="card-gold card" style={{ marginBottom: '20px' }}>
          <div className="label" style={{ marginBottom: '8px' }}>Invite code &middot; share to add friends</div>
          <div className="flex-between">
            <span className="display" style={{ fontSize: '30px', letterSpacing: '0.12em', color: 'var(--blue-bright)' }}>
              {league.invite_code}
            </span>
            <button className="btn btn-secondary" style={{ padding: '8px 16px', fontSize: '12px' }} onClick={handleCopy}>
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>

        <div className="flex-between" style={{ marginBottom: '10px' }}>
          <span style={{ fontFamily: 'var(--ff-d)', fontSize: '16px', fontWeight: '700' }}>
            {round ? `Round ${round.round_number}${currentGw ? ` · GW${currentGw.gameweek_number}` : ''}` : 'Survivor board'}
          </span>
          {currentGw && (
            currentGw.status === 'locked' || currentGw.status === 'processed'
              ? <span className="badge-gray">Picks visible</span>
              : <span className="badge-amber">Picks hidden until deadline</span>
          )}
        </div>

        {!round && (
          <div className="card"><p className="text-muted">No round is currently open for this event.</p></div>
        )}

        {round && (
          <div className="card-flat" style={{ marginBottom: '20px', overflowX: 'auto' }}>
            <table>
              <thead>
                <tr><th>Player</th><th>Status</th><th>Pick</th></tr>
              </thead>
              <tbody>
                {roundBoard.map(m => (
                  <tr key={m.user_id} style={{ background: m.user_id === authUser?.id ? 'var(--blue-dim)' : 'transparent' }}>
                    <td style={{ fontSize: '13px', fontWeight: m.user_id === authUser?.id ? '700' : '500' }}>
                      {m.display_name || 'Player'}{m.user_id === authUser?.id ? ' (you)' : ''}
                    </td>
                    <td>{statusBadge(m.entry_status, m.in_round)}</td>
                    <td style={{ fontSize: '13px' }}>
                      {m.in_round ? (
                        m.team_picked
                          ? <>{m.team_picked}{m.is_auto_pick && <span className="badge-gray" style={{ marginLeft: '6px' }}>Auto</span>}</>
                          : <span className="text-muted">&mdash;</span>
                      ) : <span className="text-muted">&mdash;</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ fontFamily: 'var(--ff-d)', fontSize: '16px', fontWeight: '700', marginBottom: '10px' }}>
          Season standings
        </div>
        <div className="card-flat" style={{ marginBottom: '20px', overflowX: 'auto' }}>
          <table>
            <thead>
              <tr><th>Player</th><th>Rounds played</th><th>Rounds won</th><th>Longest run</th></tr>
            </thead>
            <tbody>
              {standings.map(s => (
                <tr key={s.user_id} style={{ background: s.user_id === authUser?.id ? 'var(--blue-dim)' : 'transparent' }}>
                  <td style={{ fontSize: '13px', fontWeight: s.user_id === authUser?.id ? '700' : '500' }}>
                    {s.display_name || 'Player'}{s.user_id === authUser?.id ? ' (you)' : ''}
                  </td>
                  <td style={{ fontSize: '13px' }}>{s.rounds_played}</td>
                  <td style={{ fontSize: '13px' }}>{s.rounds_won}</td>
                  <td style={{ fontSize: '13px' }}>{s.longest_survival_gameweeks} GW{s.longest_survival_gameweeks === 1 ? '' : 's'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {error && <p style={{ color: 'var(--red)', fontSize: '13px', marginBottom: '16px' }}>{error}</p>}

        <div className="flex gap-8">
          <button className="btn btn-danger" disabled={leaving} onClick={handleLeave}>
            {leaving ? 'Leaving...' : 'Leave league'}
          </button>
          {league.created_by === authUser?.id && (
            <button className="btn btn-danger" disabled={deleting} onClick={handleDelete}>
              {deleting ? 'Deleting...' : 'Delete league'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
